// src/pages/RepDashboard.js
import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { format, subDays } from 'date-fns';
import { Link } from 'react-router-dom';
import { isChargeback, repCommissionOnOrder, CHARGEBACK_WINDOW_DAYS } from '../data/chargebacks';

const STATUS_COLORS = { Written: '#5599cc', Processing: '#f5a623', Completed: '#00d4a0', Cancelled: '#ff6b6b' };

export default function RepDashboard() {
  const { userProfile, currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const snap = await getDocs(
        query(collection(db, 'orders'), where('repId', '==', currentUser.uid), orderBy('createdAt', 'desc'))
      );
      setOrders(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }
    load();
  }, [currentUser]);

  const completed = orders.filter(o => o.status === 'Completed');
  const pending = orders.filter(o => o.status === 'Written' || o.status === 'Processing');
  const cancelled = orders.filter(o => o.status === 'Cancelled');
  const totalEarned = completed.reduce((a, o) => {
    const myComm = (o.commissions || []).find(c => c.repId === currentUser.uid);
    return a + (myComm?.amount || 0);
  }, 0);
  const pendingEarned = pending.reduce((a, o) => {
    const myComm = (o.commissions || []).find(c => c.repId === currentUser.uid);
    return a + (myComm?.amount || 0);
  }, 0);

  // Chargebacks: customer disconnected within 90 days of activation
  const chargebackOrders = orders.filter(isChargeback);
  const chargebackCount  = chargebackOrders.length;
  const chargebackLost   = chargebackOrders.reduce((a, o) => a + repCommissionOnOrder(o, currentUser.uid), 0);

  // Last 7 days chart
  const dayMap = {};
  for (let i = 6; i >= 0; i--) dayMap[format(subDays(new Date(), i), 'EEE')] = 0;
  orders.forEach(o => {
    if (o.createdAt?.seconds) {
      const d = format(new Date(o.createdAt.seconds * 1000), 'EEE');
      if (dayMap[d] !== undefined) dayMap[d]++;
    }
  });
  const chartData = Object.entries(dayMap).map(([day, count]) => ({ day, count }));

  if (loading) return <div style={{ padding: 60, color: '#555' }}>Loading...</div>;

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Hey, {userProfile?.name?.split(' ')[0]} 👋</h1>
          <p style={styles.sub}>Here's how you're doing · {format(new Date(), 'MMMM yyyy')}</p>
        </div>
        <div style={styles.roleBadge}>{userProfile?.role || 'Agent'}</div>
      </div>

      <div style={styles.metricsRow}>
        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>Total deals</div>
          <div style={{ ...styles.metricValue, color: '#fff' }}>{orders.length}</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>Completed</div>
          <div style={{ ...styles.metricValue, color: '#00d4a0' }}>{completed.length}</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>Earned (completed)</div>
          <div style={{ ...styles.metricValue, color: '#f5a623' }}>${Math.round(totalEarned).toLocaleString()}</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>Pending earnings</div>
          <div style={{ ...styles.metricValue, color: '#5599cc' }}>${Math.round(pendingEarned).toLocaleString()}</div>
          <div style={styles.metricSub}>When installs complete</div>
        </div>
      </div>

      <Link to="/chargebacks" style={{ textDecoration: 'none' }}>
        <div style={styles.chargebackCard}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={styles.metricLabel}>Chargebacks (disconnect within {CHARGEBACK_WINDOW_DAYS}d)</div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 16, marginTop: 6 }}>
                <span style={{ fontSize: 26, fontWeight: 600, color: '#ff6b6b' }}>{chargebackCount}</span>
                <span style={{ fontSize: 14, color: '#ff6b6b' }}>−${Math.round(chargebackLost).toLocaleString()}</span>
              </div>
            </div>
            <span style={{ fontSize: 11, color: '#666' }}>View detail →</span>
          </div>
        </div>
      </Link>

      <div style={styles.twoCol}>
        <div style={styles.chartCard}>
          <div style={styles.cardTitle}>Deals this week</div>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={chartData}>
              <XAxis dataKey="day" tick={{ fill: '#555', fontSize: 11 }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fill: '#555', fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip contentStyle={{ background: '#1a1a24', border: '1px solid #2a2a3a', borderRadius: 8, fontSize: 12, color: '#fff' }} />
              <Bar dataKey="count" fill="#00d4a0" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={styles.chartCard}>
          <div style={styles.cardTitle}>My status breakdown</div>
          <div style={styles.statusList}>
            {[
              { label: 'Written', count: orders.filter(o => o.status === 'Written').length, color: '#5599cc' },
              { label: 'Completed', count: completed.length, color: '#00d4a0' },
              { label: 'Pending', count: pending.length, color: '#f5a623' },
              { label: 'Cancelled', count: cancelled.length, color: '#ff6b6b' },
            ].map(s => (
              <div key={s.label} style={styles.statusRow}>
                <div style={{ ...styles.statusDot, background: s.color }} />
                <span style={styles.statusLabel}>{s.label}</span>
                <span style={{ ...styles.statusCount, color: s.color }}>{s.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={styles.tableCard}>
        <div style={styles.cardTitle}>Recent orders</div>
        <table style={styles.table}>
          <thead>
            <tr>{['Customer', 'Product', 'My commission', 'Status', 'Date'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {orders.slice(0, 15).map(o => {
              const myComm = (o.commissions || []).find(c => c.repId === currentUser.uid);
              return (
                <tr key={o.id}>
                  <td style={styles.td}>{o.customerName}</td>
                  <td style={{ ...styles.td, color: '#666' }}>{o.productName}</td>
                  <td style={{ ...styles.td, color: '#00d4a0' }}>${Math.round(myComm?.amount || 0).toLocaleString()}</td>
                  <td style={styles.td}>
                    <span style={{ fontSize: 11, fontWeight: 500, padding: '3px 9px', borderRadius: 6, color: STATUS_COLORS[o.status], background: STATUS_COLORS[o.status] + '22' }}>
                      {o.status}
                    </span>
                  </td>
                  <td style={{ ...styles.td, color: '#555' }}>{o.date || '—'}</td>
                </tr>
              );
            })}
            {orders.length === 0 && (
              <tr><td colSpan={5} style={{ ...styles.td, textAlign: 'center', color: '#444', padding: 32 }}>No orders yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: '20px 16px', color: '#fff', width: '100%', boxSizing: 'border-box' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title: { fontSize: 24, fontWeight: 600, margin: 0, letterSpacing: '-0.3px' },
  sub: { fontSize: 13, color: '#555', margin: '4px 0 0' },
  roleBadge: { fontSize: 11, fontWeight: 500, padding: '5px 12px', borderRadius: 20, background: '#1a2e24', color: '#00d4a0', textTransform: 'capitalize' },
  metricsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 },
  metricCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, padding: '18px 20px' },
  chargebackCard: { background: '#13131a', border: '1px solid #2a1515', borderRadius: 12, padding: '16px 20px', marginBottom: 20, cursor: 'pointer' },
  metricLabel: { fontSize: 11, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 },
  metricValue: { fontSize: 28, fontWeight: 600, letterSpacing: '-0.5px' },
  metricSub: { fontSize: 11, color: '#444', marginTop: 4 },
  twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 20 },
  chartCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, padding: '18px 20px' },
  cardTitle: { fontSize: 12, color: '#888', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 14 },
  statusList: { display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 8 },
  statusRow: { display: 'flex', alignItems: 'center', gap: 10 },
  statusDot: { width: 8, height: 8, borderRadius: '50%', flexShrink: 0 },
  statusLabel: { fontSize: 13, color: '#888', flex: 1 },
  statusCount: { fontSize: 18, fontWeight: 600 },
  tableCard: { background: '#13131a', border: '1px solid #1e1e2a', borderRadius: 12, padding: '18px 20px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 11, color: '#555', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid #1e1e2a' },
  td: { padding: '11px 12px', fontSize: 13, color: '#ccc', borderBottom: '1px solid #111' },
};
